'use client';

import React, { useState, useRef, useEffect } from 'react';

interface TooltipProps {
  content: string;
  children?: React.ReactNode;
  className?: string;
  delay?: number;
}

export function Tooltip({ content, children, className = '', delay = 100 }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<'top' | 'bottom'>('top');
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceAbove = rect.top;

      // Show tooltip below if not enough space above
      setPosition(spaceAbove < 150 ? 'bottom' : 'top');
    }
  }, [isVisible]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative inline-block">
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`inline-block ${className}`}
      >
        {children || (
          <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-blue-600 hover:text-blue-700 cursor-help rounded-full border-2 border-blue-400 hover:border-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors duration-75">
            ?
          </span>
        )}
      </div>

      {isVisible && (
        <div
          ref={tooltipRef}
          className={`absolute z-50 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg max-w-xs whitespace-normal transition-opacity duration-100 ${
            position === 'top'
              ? 'bottom-full mb-2 left-1/2 transform -translate-x-1/2'
              : 'top-full mt-2 left-1/2 transform -translate-x-1/2'
          }`}
          style={{ minWidth: '200px' }}
        >
          {content}
          <div
            className={`absolute left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45 ${
              position === 'top' ? '-bottom-1' : '-top-1'
            }`}
          />
        </div>
      )}
    </div>
  );
}

interface InfoIconProps {
  tooltip: string;
  delay?: number;
}

export function InfoIcon({ tooltip, delay = 100 }: InfoIconProps) {
  return (
    <Tooltip content={tooltip} delay={delay}>
      <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-blue-600 hover:text-blue-700 cursor-help rounded-full border-2 border-blue-400 hover:border-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors duration-75">
        ?
      </span>
    </Tooltip>
  );
}
